import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { NURSE_SCHEDULE_QUERY_KEY } from "../../../hooks/useInvalidateNurseQueries";
import { backendService } from "../../../services/backend.service";
import type { OpenStatusDecision } from "./types";
import { StatusDecisionChoice, type StatusDecisionDirection, type StatusDecisionReason } from "./types";

export const OPEN_STATUS_DECISIONS_QUERY_KEY = "team-open-status-decisions";

export const useOpenStatusDecisions = (departmentId?: string) => {
	const { track, trackError } = useAnalytics();

	return useQuery<OpenStatusDecision[] | undefined>({
		queryKey: [OPEN_STATUS_DECISIONS_QUERY_KEY, departmentId],
		queryFn: async () => {
			if (!departmentId) {
				return;
			}
			track("team_status_decisions_fetch_start", { department_id: departmentId });
			try {
				const { data } = await backendService.getOpenStatusDecisions(departmentId);
				track("team_status_decisions_fetch_success", { department_id: departmentId, count: data.length });
				return data;
			} catch (error) {
				trackError("team_status_decisions_fetch_error", { error_message: (error as Error)?.message });
				throw error;
			}
		},
		enabled: !!departmentId,
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
	});
};

type ApplyDecisionArgs = {
	decisionId: string;
	choice: StatusDecisionChoice;
	nurseId: number;
	reason: StatusDecisionReason;
	direction: StatusDecisionDirection;
};

export const useApplyStatusDecision = (departmentId?: string) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { refreshNursesData } = useHospitalData();

	return useMutation<void, Error, ApplyDecisionArgs>({
		mutationFn: async ({ decisionId, choice }) => {
			await backendService.applyStatusDecision(decisionId, choice);
		},
		onSuccess: async (_data, { choice, reason, direction, nurseId }) => {
			track("team_status_decision_applied", { choice, reason, direction, nurseId, department_id: departmentId });
			await queryClient.invalidateQueries({ queryKey: [OPEN_STATUS_DECISIONS_QUERY_KEY, departmentId] });
			if (departmentId && choice === StatusDecisionChoice.Changed) {
				await refreshNursesData(departmentId);
				// A "Changed" decision deactivates or reactivates the nurse, which removes or restores shifts.
				await queryClient.invalidateQueries({ queryKey: NURSE_SCHEDULE_QUERY_KEY });
			}
			showSnackbar(t("team.status.decision.applied"), "success");
		},
		onError: (error) => {
			trackError("team_status_decision_apply_error", { error_message: error?.message });
			showSnackbar(t("team.status.decision.apply.error"), "error");
		},
	});
};
